import { useMemo } from 'react';
import { buildCategoryTreeFromCounts } from 'markcat-domain';
import type { SmartFilterId, SystemCategoryId } from 'markcat-domain';
import { useBookmarkSummary, useCategoryMeta } from '../queries/useCategories';
import { useCategoryActions, type NotifyFn } from './useCategoryActions';

const EMPTY_SYSTEM_COUNTS: Record<SystemCategoryId, number> = {
  dead_links: 0,
  broken_links: 0,
  bot_protected: 0,
  failed: 0,
  low_confidence: 0,
  fetch_failed: 0,
};

const EMPTY_SMART_FILTER_COUNTS: Record<SmartFilterId, number> = {
  favourites: 0,
  recently_added: 0,
  needs_review: 0,
  no_screenshot: 0,
  title_only: 0,
  uncategorized: 0,
};

const EMPTY_TAG_COUNTS: Record<string, number> = {};

/** Tenant-wide category data and mutations. Mount once in each application host. */
export function useCategoryModel(notify: NotifyFn) {
  const { data: summary } = useBookmarkSummary();
  const { data: metadata } = useCategoryMeta();
  const actions = useCategoryActions(notify);
  const tree = useMemo(
    () => buildCategoryTreeFromCounts(summary?.category_counts ?? {}, metadata),
    [summary?.category_counts, metadata],
  );

  return {
    tree,
    systemCounts: summary?.system_counts ?? EMPTY_SYSTEM_COUNTS,
    smartFilterCounts: summary?.smart_filter_counts ?? EMPTY_SMART_FILTER_COUNTS,
    tagCounts: summary?.tag_counts ?? EMPTY_TAG_COUNTS,
    ...actions,
  };
}

export type CategoryModel = ReturnType<typeof useCategoryModel>;
